import React, {createContext, useCallback, useContext, useEffect, useState, useSyncExternalStore} from 'react';

import {VITE_NO_EXTENSION_CHECK} from '../../config';
import {createExtensionCommandQueue, type ExtensionCommandQueue} from '../../services/extension/commandQueue';
import {createExtensionConnection, type ExtensionConnection} from '../../services/extension/connection';
import {
  createExtensionTransport,
  type ExtensionMessageTarget,
  type ExtensionTransport,
} from '../../services/extension/transport';

// Services extension (transport + connexion + file), instanciés une fois par le Provider.
// Le transport est l'unique seam couplé au navigateur : les tests montent le Provider avec
// des services bâtis sur un faux transport → toute la couche est testable sans extension.
export type ExtensionServices = {
  transport: ExtensionTransport;
  connection: ExtensionConnection;
  commandQueue: ExtensionCommandQueue;
};

export function createExtensionServices(target?: ExtensionMessageTarget): ExtensionServices {
  const transport = createExtensionTransport(target);
  const connection = createExtensionConnection(transport);
  const commandQueue = createExtensionCommandQueue({transport, isAvailable: connection.isAvailable});
  return {transport, connection, commandQueue};
}

const ExtensionContext = createContext<ExtensionServices | undefined>(undefined);

export function ExtensionProvider({services, children}: {services?: ExtensionServices; children: React.ReactNode}) {
  // Instanciation unique et stable (pas de recréation sur re-render, contrairement à useMemo qui
  // pourrait recalculer). `owned` = on a créé le transport, donc on doit le disposer au démontage.
  const [instance] = useState(() => ({
    value: services ?? createExtensionServices(),
    owned: services === undefined,
  }));

  useEffect(() => {
    // Détection de l'extension au montage (remplace le dispatch(checkInstall) du DashboardLayout).
    // Respecte VITE_NO_EXTENSION_CHECK : quand le flag est actif, aucune détection n'est lancée.
    if (VITE_NO_EXTENSION_CHECK === false) {
      void instance.value.connection.checkInstall();
    }
    return () => {
      if (instance.owned) {
        instance.value.transport.dispose();
      }
    };
  }, [instance]);

  return <ExtensionContext.Provider value={instance.value}>{children}</ExtensionContext.Provider>;
}

export function useExtensionServices(): ExtensionServices {
  const services = useContext(ExtensionContext);
  if (services === undefined) {
    throw new Error('useExtension* doit être utilisé dans un <ExtensionProvider>');
  }
  return services;
}

export function useExtensionConnection() {
  const {connection} = useExtensionServices();
  const snapshot = useSyncExternalStore(connection.subscribe, connection.getSnapshot);

  const checkInstall = useCallback((options?: {silent?: boolean}) => connection.checkInstall(options), [connection]);
  const reloadSession = useCallback(() => connection.reloadSession(), [connection]);
  const login = useCallback(
    (credentials: {email: string; password: string}) => connection.login(credentials),
    [connection],
  );
  const openExtensionPopup = useCallback(() => connection.openPopup(), [connection]);

  return {
    ...snapshot,
    isSearching: snapshot.status === 'searching',
    isExtensionFound: snapshot.status === 'found',
    isExtensionNotFound: snapshot.status === 'not_found',
    isAvailable: snapshot.status !== 'not_found',
    checkInstall,
    reloadSession,
    login,
    openExtensionPopup,
  };
}

export function useExtensionCommands() {
  const {commandQueue} = useExtensionServices();
  const snapshot = useSyncExternalStore(commandQueue.subscribe, commandQueue.getSnapshot);

  const checkQueue = useCallback(() => commandQueue.checkQueue(), [commandQueue]);
  const enqueue = useCallback((send: () => Promise<string | undefined>) => commandQueue.enqueue(send), [commandQueue]);
  const dismissFinishedCommand = useCallback(
    (commandId: string) => commandQueue.dismissFinishedCommand(commandId),
    [commandQueue],
  );
  const getCommandById = useCallback(
    (commandId: string | undefined) =>
      commandId === undefined ? undefined : snapshot.commandStack.find((command) => command.commandId === commandId),
    [snapshot],
  );

  return {
    commandStack: snapshot.commandStack,
    currentCommandId: snapshot.currentCommandId,
    checkQueue,
    enqueue,
    dismissFinishedCommand,
    getCommandById,
  };
}
